import React, { useMemo, useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../../company-management/hooks/usePermissions';
import { PERMISSIONS } from '../../company-management/constants/permissions';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import AccountSettings from './pages/AccountSettings';
import ManageSubscription from './pages/billing/ManageSubscription';
import BusinessInformation from './pages/BusinessInformation';
import BusinessHours from './pages/BusinessHours';
import AuthorizeSettings from './pages/AuthorizeSettings';
import DeluxeSettings from './pages/DeluxeSettings';
import NotificationsRemindersSettings from './pages/notifications-reminders/NotificationsRemindersSettings';
import ServicesCategoriesSettings from './pages/services-categories/ServicesCategoriesSettings';
import PaymentSettingsLanding from './pages/PaymentSettingsLanding';
import PaymentGatewayEnvironmentSettings from './pages/payment-environment/PaymentGatewayEnvironmentSettings';
import MyServicesSettingsPage from './pages/MyServicesSettingsPage';
import BookingSettings from './pages/booking/BookingSettings';
import { useTutorial } from '../tutorial/TutorialContext';
import LandingPageSettings from './pages/LandingPageSettings';
import ClientExperienceSettings from './pages/client-experience/ClientExperienceSettings';
import LocationsSettingsPage from '../../locations/settings/LocationsSettingsPage';
import LocationEditPage from '../../locations/settings/LocationEditPage';
import { parseLocationSettingsRoute } from '../../locations/settings/locationSettingsPaths';
import {
  buildSettingsSectionPath,
  getCanonicalSettingsHref,
  getSettingsPathFromLocation,
  parseSettingsSectionPath,
} from './section-paths/settingsSectionPaths';
import './styles/DesktopSidebar.css';

const getMenuItems = () => [
  {
    id: 'account',
    group: 'Personal',
    label: 'Account Settings',
    description: 'Manage your profile, sign-in, and privacy preferences',
    alwaysShow: true
  },
  {
    id: 'billing',
    group: 'Personal',
    label: 'Billing',
    description: 'Manage your subscription, payment method, and invoices',
    ownerOnly: true,
    alwaysShow: true
  },
  {
    id: 'booking',
    group: 'Business',
    label: 'Booking Settings',
    description: 'Control booking rules like double booking and lead times',
    requiresEdit: true
  },
  {
    id: 'business',
    group: 'Business',
    label: 'Business Information',
    description: 'Update your business details and contact information',
    requiresEdit: true
  },
  {
    id: 'hours',
    group: 'Business',
    label: 'Business Hours',
    description: 'Set your working hours and availability',
    requiresEdit: true
  },
  {
    id: 'locations',
    group: 'Business',
    label: 'Locations',
    description: 'Manage business locations, addresses, and site hours',
    requiresEdit: true
  },
  {
    id: 'services-categories',
    group: 'Business',
    label: 'Services',
    description: 'Manage your bookable services and pricing',
    requiresEdit: true
  },
  {
    id: 'client-experience',
    group: 'Business',
    label: 'Client Experience',
    description: 'Manage your website and shared client-facing links',
    requiresEdit: true
  },
  {
    id: 'payments',
    group: 'Payments',
    label: 'Payment Settings',
    description: 'Manage payment methods and integrations',
    requiresEdit: true
  },
  {
    id: 'notifications',
    group: 'Messaging',
    label: 'Notifications & Reminders',
    description: 'Customize email/sms notifications and templates',
    requiresEdit: true
  }
];

const NewSettings = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const initialSection = parseSettingsSectionPath(location.pathname, location.search);
  const [selectedSection, setSelectedSection] = useState(initialSection.section);
  const [selectedParentSection, setSelectedParentSection] = useState(initialSection.parent);
  const [notificationsSubtab, setNotificationsSubtab] = useState(initialSection.notificationsTab || 'email');
  const [hasUnsavedSettingsChanges, setHasUnsavedSettingsChanges] = useState(false);
  const [menuQuery, setMenuQuery] = useState('');
  const [expandedParents, setExpandedParents] = useState({});
  const { hasPermission } = usePermissions();
  const { user, companyData } = useAuth();
  const { isDismissed, restoreTutorial } = useTutorial();

  // Scroll to top when section changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [selectedSection]);

  const openSettingsSection = (section, { tab, replace = false, state } = {}) => {
    const nextPath = buildSettingsSectionPath(section, { tab });
    if (nextPath === getCanonicalSettingsHref(location.pathname, location.search)) {
      return;
    }
    navigate(nextPath, { replace, state });
  };

  // Keep the URL as the source of truth so refresh stays on the same settings page
  useEffect(() => {
    const next = getSettingsPathFromLocation(location);
    const currentHref = `${location.pathname}${location.search}`;

    if (!parseLocationSettingsRoute(location.pathname) && next.path !== currentHref) {
      navigate(next.path, { replace: true, state: location.state });
      return;
    }

    setSelectedSection(next.section);
    setSelectedParentSection(next.parent);
    if (next.notificationsTab) {
      setNotificationsSubtab(next.notificationsTab);
    }
  }, [location, navigate]);

  const locationSettingsRoute = parseLocationSettingsRoute(location.pathname);

  // Check for settings view permission
  if (!hasPermission(PERMISSIONS.SETTINGS.VIEW)) {
    return <Navigate to="/unauthorized" replace />;
  }

  const canEditSettings = hasPermission(PERMISSIONS.SETTINGS.EDIT);
  const canViewAllSettings = hasPermission(PERMISSIONS.SETTINGS.VIEW_ALL);
  const showMyServices =
    companyData?.settings?.allowEmployeeSelfServiceAssignment === true &&
    String(user?.role || '').toLowerCase() !== 'owner';
  const visibleMenuItems = useMemo(() => {
    const isOwner = String(user?.role || '').toLowerCase() === 'owner';
    const baseItems = getMenuItems().filter((item) => {
      if (item.ownerOnly && !isOwner) return false;
      return item.alwaysShow || canViewAllSettings || (!item.requiresEdit || canEditSettings);
    });

    const personalItems = [];
    if (showMyServices) {
      personalItems.push({
        id: 'my-services',
        group: 'Personal',
        label: 'My Services',
        description: 'Choose which services you are available to perform',
        alwaysShow: true,
      });
    }

    if (personalItems.length === 0) {
      return baseItems;
    }

    const accountIndex = baseItems.findIndex((item) => item.id === 'account');
    if (accountIndex === -1) {
      return [...personalItems, ...baseItems];
    }

    return [
      ...baseItems.slice(0, accountIndex + 1),
      ...personalItems,
      ...baseItems.slice(accountIndex + 1),
    ];
  }, [canEditSettings, canViewAllSettings, showMyServices, user?.role]);

  const filteredMenuItems = useMemo(() => {
    const q = menuQuery.trim().toLowerCase();
    if (!q) return visibleMenuItems;

    return visibleMenuItems.filter((item) => {
      const inTopLevel =
        item.label.toLowerCase().includes(q) ||
        (item.description || '').toLowerCase().includes(q) ||
        (item.group || '').toLowerCase().includes(q);

      if (inTopLevel) return true;

      if (!item.subItems?.length) return false;
      return item.subItems.some((subItem) => subItem.label.toLowerCase().includes(q));
    });
  }, [menuQuery, visibleMenuItems]);

  const confirmSettingsLeave = () => {
    if (!hasUnsavedSettingsChanges) {
      return true;
    }

    const shouldLeave = window.confirm('You have unsaved changes. Save them before leaving this page?');
    if (shouldLeave) {
      setHasUnsavedSettingsChanges(false);
    }

    return shouldLeave;
  };

  const handleMenuClick = (itemId) => {
    if (!confirmSettingsLeave()) {
      return;
    }

    const menuItem =
      filteredMenuItems.find((item) => item.id === itemId) ||
      visibleMenuItems.find((item) => item.id === itemId);

    if (menuItem?.subItems?.length) {
      setSelectedParentSection(itemId);

      // Toggle expand/collapse. Only navigate when expanding (not when collapsing).
      // Also collapse all other expanded items
      setExpandedParents((prev) => {
        const nextExpanded = !prev[itemId];
        const newExpanded = {};

        // Close all other expanded items
        Object.keys(prev).forEach((key) => {
          if (key !== itemId) {
            newExpanded[key] = false;
          }
        });

        // Set the clicked item's expanded state
        newExpanded[itemId] = nextExpanded;

        if (nextExpanded) {
          const isCurrentlyInside = menuItem.subItems.some((s) => s.id === selectedSection);
          const nextSection = isCurrentlyInside ? selectedSection : menuItem.subItems[0].id;
          openSettingsSection(nextSection);
        }

        return newExpanded;
      });
      return;
    }

    // When clicking on an item without subitems, collapse all expanded items
    setExpandedParents({});
    if (itemId === 'notifications') {
      setNotificationsSubtab('email');
    }
    openSettingsSection(itemId, { tab: itemId === 'notifications' ? 'email' : undefined });
  };

  const getContent = () => {
    if (locationSettingsRoute) {
      return <LocationEditPage embedded />;
    }

    switch (selectedSection) {
      case 'account':
        return <AccountSettings />;
      case 'billing':
        return String(user?.role || '').toLowerCase() === 'owner'
          ? <ManageSubscription embedded />
          : null;
      case 'my-services':
        return <MyServicesSettingsPage />;
      case 'business':
        return <BusinessInformation />;
      case 'hours':
        return <BusinessHours />;
      case 'locations':
        return <LocationsSettingsPage />;
      case 'booking':
        return <BookingSettings />;
      case 'client-experience':
      case 'portal':
        return <ClientExperienceSettings onUnsavedChangesChange={setHasUnsavedSettingsChanges} />;
      case 'payments':
      case 'deposits':
      case 'tips':
        return (
          <PaymentSettingsLanding 
            onSelectOption={(section) => {
              openSettingsSection(section);
            }}
          />
        );
      case 'deluxe':
        return (
          <DeluxeSettings
            onBackToPayments={() => {
              openSettingsSection('payments');
            }}
          />
        );
      case 'authorize':
        return (
          <AuthorizeSettings 
            onBackToPayments={() => {
              openSettingsSection('payments');
            }}
          />
        );
      case 'gateway-environments':
        if (!user?.isSpecialAdmin) {
          return (
            <Box sx={{ p: 3 }}>
              <Typography variant="body1" color="text.secondary">
                This section is available to special admins only.
              </Typography>
            </Box>
          );
        }
        return (
          <PaymentGatewayEnvironmentSettings
            onBackToPayments={() => {
              openSettingsSection('payments');
            }}
          />
        );
      case 'services-categories':
        return canEditSettings ? <ServicesCategoriesSettings /> : null;
      case 'notifications':
        return (
          <NotificationsRemindersSettings
            messagingTab={notificationsSubtab}
            onMessagingTabChange={(tab) => {
              setNotificationsSubtab(tab);
              openSettingsSection('notifications', { tab, replace: true, state: location.state });
            }}
          />
        );
      case 'landing-page':
        return <LandingPageSettings onUnsavedChangesChange={setHasUnsavedSettingsChanges} />;
      default:
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>
              {visibleMenuItems.find(item => item.id === selectedSection)?.label}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Content for {selectedSection} will go here
            </Typography>
          </Box>
        );
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 0,
        p: 0,
        minHeight: '100vh',
        background: 'transparent',
      }}
    >
      {/* Left Navigation Panel - custom sidebar */}
      <Box sx={{ width: 420, backgroundColor: 'transparent' }}>
        <nav className="settings-sidebar" aria-label="Settings navigation">
          <div className="settings-nav-header">
            <div className="settings-nav-header-top">
              <div className="settings-nav-title">Settings</div>
              <div className="settings-nav-subtitle">Account, business, payments, and reminders</div>
            </div>
            <label className="settings-nav-search" aria-label="Search settings">
              <input
                value={menuQuery}
                onChange={(e) => setMenuQuery(e.target.value)}
                placeholder="Search settings…"
                type="search"
              />
            </label>
          </div>

          {filteredMenuItems.length === 0 ? (
            <div className="settings-nav-empty">
              <div className="settings-nav-empty-title">No matches</div>
              <div className="settings-nav-empty-subtitle">Try a different keyword.</div>
            </div>
          ) : (
            <div className="settings-nav-groups">
              {Object.entries(
                filteredMenuItems.reduce((acc, item) => {
                  const group = item.group || 'Other';
                  if (!acc[group]) acc[group] = [];
                  acc[group].push(item);
                  return acc;
                }, {})
              ).map(([group, items]) => (
                <section key={group} className="settings-nav-group" aria-label={group}>
                  <div className="settings-nav-group-title">{group}</div>
                  <ul className="settings-nav-list">
                    {items.map((item) => {
                      const isActiveParent = selectedParentSection === item.id;
                      const q = menuQuery.trim().toLowerCase();
                      const hasSubItems = item.subItems && item.subItems.length > 0;
                      const subItemsToShow = !hasSubItems
                        ? []
                        : !q
                          ? item.subItems
                          : item.subItems.filter((s) => s.label.toLowerCase().includes(q));

                      const isExpanded = !!expandedParents[item.id];
                      const showSubItems = hasSubItems && (!!q || isExpanded);

                      return (
                        <li key={item.id} className="settings-nav-item">
                          <button
                            className={`settings-nav-button ${isActiveParent ? 'active' : ''}`}
                            onClick={() => handleMenuClick(item.id)}
                            type="button"
                            aria-current={isActiveParent ? 'page' : undefined}
                          >
                            <span className="settings-nav-text">
                              <span className="settings-nav-label">{item.label}</span>
                              {item.description && <span className="settings-nav-desc">{item.description}</span>}
                            </span>
                          </button>

                          {showSubItems && subItemsToShow.length > 0 && (
                            <ul className="settings-nav-sublist" aria-label={`${item.label} pages`}>
                              {subItemsToShow.map((subItem) => (
                                <li key={subItem.id} className="settings-nav-subitem">
                                  <button
                                    className={`settings-nav-subbutton ${selectedSection === subItem.id ? 'active' : ''}`}
                                    onClick={() => {
                                      if (!confirmSettingsLeave()) {
                                        return;
                                      }
                                      openSettingsSection(subItem.id);
                                    }}
                                    type="button"
                                    aria-current={selectedSection === subItem.id ? 'page' : undefined}
                                  >
                                    {subItem.label}
                                  </button>
                                </li>
                              ))}
                            </ul>
                          )}
                        </li>
                      );
                    })}

                    {(() => {
                      const isOwner = (user?.role || '').toLowerCase() === 'owner';
                      const canAdminSettings = isOwner || hasPermission(PERMISSIONS.SETTINGS.EDIT) || hasPermission(PERMISSIONS.SETTINGS.VIEW_ALL);
                      const canShowSetupGuide = isDismissed && canAdminSettings;
                      return canShowSetupGuide;
                    })() && group === 'Personal' && (
                      <li className="settings-nav-item">
                        <button
                          type="button"
                          onClick={restoreTutorial}
                          className="settings-nav-button"
                        >
                          <span className="settings-nav-text">
                            <span className="settings-nav-label">Show Setup Guide</span>
                            <span className="settings-nav-desc">Restore the setup guide to help you configure your settings</span>
                          </span>
                        </button>
                      </li>
                    )}
                  </ul>
                </section>
              ))}
            </div>
          )}
        </nav>
      </Box>

      {/* Right Content Panel */}
      <Box
        className="settings-right-content"
        sx={{
          flex: 1,
          backgroundColor: 'transparent',
        }}
      >
        {/* Main Content */}
        <Box sx={{ 
          p: 3,
          pt: 2,
        }}>
          {getContent()}
        </Box>
      </Box>
    </Box>
  );
};

export default NewSettings; 